// ---------------------------------------------------------------------------
// Digital Notice Board — pure helpers (no React, no I/O)
// ---------------------------------------------------------------------------
import {
    CANVAS_PRESETS,
    DEFAULT_SLIDE_DURATION,
    ELEMENT_TYPES,
} from "../constants/NoticeBoardConstants";

let seq = 0;
export const uid = (prefix = "id") =>
    `${prefix}_${Date.now().toString(36)}_${(seq++).toString(36)}`;

// Reads the logged-in school's name + address from localStorage `userInfo`
// (seeded at login). Field names mirror the rest of the app — `clientName` is
// the canonical school name — with defensive fallbacks. Returns empty strings
// when nothing is stored so callers can substitute their own placeholder.
export function getSchoolIdentity() {
    let userInfo = {};
    try {
        userInfo = JSON.parse(localStorage.getItem("userInfo")) || {};
    } catch (e) {
        userInfo = {};
    }
    const name =
        userInfo.clientName || userInfo.schoolName || userInfo.name || "";
    const address =
        userInfo.clientAddress || userInfo.schoolAddress || userInfo.address || "";
    return {name, address};
}

export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export const getPreset = (id) =>
    CANVAS_PRESETS.find((p) => p.id === id) || CANVAS_PRESETS[0];

// Sensible starting geometry + styling for each element kind, expressed in the
// slide's LOGICAL units so a freshly dropped element looks right at any preset.
export function makeElement(type, canvas) {
    const cw = canvas.width;
    const ch = canvas.height;
    const base = {
        id: uid("el"),
        type,
        x: Math.round(cw * 0.28),
        y: Math.round(ch * 0.38),
        w: Math.round(cw * 0.44),
        h: Math.round(ch * 0.18),
        rotation: 0,
        opacity: 1,
    };

    switch (type) {
        case ELEMENT_TYPES.HEADING:
            return {
                ...base,
                h: Math.round(ch * 0.14),
                text: getSchoolIdentity().name || "School Name",
                fontSize: Math.round(ch * 0.09),
                fontWeight: 700,
                fontFamily: "Raleway, sans-serif",
                color: "#1A202C",
                align: "center",
                bg: "transparent",
            };
        case ELEMENT_TYPES.TEXT:
            return {
                ...base,
                text: "Add your text here",
                fontSize: Math.round(ch * 0.045),
                fontWeight: 400,
                fontFamily: "Roboto, sans-serif",
                color: "#2D3748",
                align: "left",
                bg: "transparent",
            };
        case ELEMENT_TYPES.MARQUEE:
            return {
                ...base,
                y: Math.round(ch * 0.88),
                x: 0,
                w: cw,
                h: Math.round(ch * 0.1),
                text: "Welcome to our school • Notices scroll here • Stay tuned",
                fontSize: Math.round(ch * 0.05),
                fontWeight: 600,
                fontFamily: "Roboto, sans-serif",
                color: "#FFFFFF",
                bg: "#3182CE",
                speed: 12,
            };
        case ELEMENT_TYPES.NOTICE:
            return {
                ...base,
                x: Math.round(cw * 0.1),
                w: Math.round(cw * 0.8),
                h: Math.round(ch * 0.36),
                title: "Notice Title",
                body: "Type the notice details here. Keep it short and readable from a distance.",
                titleSize: Math.round(ch * 0.06),
                bodySize: Math.round(ch * 0.04),
                color: "#1A202C",
                accent: "#3182CE",
                bg: "#FFFFFF",
                radius: 18,
            };
        case ELEMENT_TYPES.IMAGE:
            return {
                ...base,
                x: Math.round(cw * 0.34),
                w: Math.round(cw * 0.32),
                h: Math.round(ch * 0.32),
                src: "",
                fit: "cover",
                radius: 12,
            };
        case ELEMENT_TYPES.ICON:
            return {
                ...base,
                x: Math.round(cw * 0.44),
                w: Math.round(ch * 0.16),
                h: Math.round(ch * 0.16),
                icon: "bell",
                color: "#3182CE",
            };
        case ELEMENT_TYPES.SHAPE:
            return {
                ...base,
                shape: "rect",
                fill: "#3182CE",
                radius: 16,
            };
        case ELEMENT_TYPES.CLOCK:
            return {
                ...base,
                x: Math.round(cw * 0.62),
                y: Math.round(ch * 0.06),
                w: Math.round(cw * 0.32),
                h: Math.round(ch * 0.12),
                fontSize: Math.round(ch * 0.09),
                fontWeight: 700,
                fontFamily: "'Courier New', monospace",
                color: "#1A202C",
                align: "right",
                seconds: true,
            };
        case ELEMENT_TYPES.DATE:
            return {
                ...base,
                x: Math.round(cw * 0.04),
                y: Math.round(ch * 0.06),
                w: Math.round(cw * 0.4),
                h: Math.round(ch * 0.1),
                fontSize: Math.round(ch * 0.05),
                fontWeight: 500,
                fontFamily: "Roboto, sans-serif",
                color: "#2D3748",
                align: "left",
            };
        default:
            return base;
    }
}

export function makeSlide(canvas, name) {
    return {
        id: uid("slide"),
        name: name || "Slide",
        background: "#F7FAFC",
        duration: DEFAULT_SLIDE_DURATION,
        elements: [],
    };
}

export function makeBoard(name, presetId = "16:9") {
    const preset = getPreset(presetId);
    const canvas = {preset: preset.id, width: preset.width, height: preset.height};
    return {
        id: uid("board"),
        name: name || "Untitled Board",
        canvas,
        slides: [makeSlide(canvas, "Slide 1")],
        updatedAt: Date.now(),
    };
}

// Reorder helper for slide/element lists.
export function moveItem(list, from, to) {
    if (to < 0 || to >= list.length) return list;
    const next = list.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
}

export const formatClock = (d, withSeconds) => {
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const s = String(d.getSeconds()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return withSeconds ? `${h}:${m}:${s} ${ampm}` : `${h}:${m} ${ampm}`;
};

export const formatDate = (d) =>
    d.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });
